import { createAsyncThunk } from '@reduxjs/toolkit';
import { getMovementsFiltersRequest, getMovementsRequest } from './movementsApi';
import { buildMovementsParams } from './movementsUtils';
import { EXPORT_LIMIT, EXPORT_PAGE_SIZE } from './movementsExport';
import { selectCurrentWarehouseId } from '../warehouseContext/warehouseContextSlice';
import { extractApiError } from '../../utils/errors';

/**
 * The page of movements the filters describe, in the warehouse in use — or in
 * every warehouse in reach when none is picked.
 *
 * @returns {Promise<{count: number, results: Array<object>}>}
 */
export const fetchMovements = createAsyncThunk(
  'movements/fetchMovements',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const response = await getMovementsRequest(
        buildMovementsParams(state.movements.filters, selectCurrentWarehouseId(state))
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching movements:', error);
      return rejectWithValue(extractApiError(error, 'No se pudieron obtener los movimientos'));
    }
  }
);

/**
 * Who can be filtered by: the people who left a movement here. Asked again when
 * the warehouse changes, because another warehouse is another floor.
 *
 * @returns {Promise<{actors: Array<object>, has_system: boolean, movement_types: Array<object>}>}
 */
export const fetchMovementsFilters = createAsyncThunk(
  'movements/fetchMovementsFilters',
  async (_, { getState, rejectWithValue }) => {
    try {
      const warehouseId = selectCurrentWarehouseId(getState());
      const response = await getMovementsFiltersRequest(warehouseId ? { warehouse: warehouseId } : {});
      return response.data;
    } catch (error) {
      console.error('Error fetching movements filters:', error);
      return rejectWithValue(extractApiError(error, 'No se pudieron obtener los filtros'));
    }
  }
);

/**
 * Every movement the filters describe, for the Excel: the server pages at a
 * hundred, so this walks the pages until the filter is exhausted or the export
 * limit is reached. The table is not touched.
 *
 * @returns {Promise<{rows: Array<object>, total: number}>}
 */
export const exportMovements = createAsyncThunk(
  'movements/exportMovements',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const base = buildMovementsParams(state.movements.filters, selectCurrentWarehouseId(state));
      const rows = [];
      let total = 0;
      for (let page = 1; rows.length < EXPORT_LIMIT; page += 1) {
        const response = await getMovementsRequest({ ...base, page, page_size: EXPORT_PAGE_SIZE });
        const results = response.data?.results || [];
        total = response.data?.count || 0;
        rows.push(...results);
        if (results.length < EXPORT_PAGE_SIZE || rows.length >= total) break;
      }
      return { rows: rows.slice(0, EXPORT_LIMIT), total };
    } catch (error) {
      console.error('Error exporting movements:', error);
      return rejectWithValue(extractApiError(error, 'No se pudieron exportar los movimientos'));
    }
  }
);
